import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchActivityListingDataRequest,
  selectEnrichedActivityListingData,
  selectPagingModel,
  selectIsLoading as selectIsActivityListingLoading,
} from "./slice";
import CardListing from '../../components/CardListing';
import {ListingViewConstants} from '../../common/constants';
import LoaderComponent from '../../components/Loader';
import { debounce } from 'lodash';
import Toast from '../../components/Toast';
import { hideToast } from '../SpockViewPage/actions/action';
import { showDialogueBox } from '../../components/DialogueBox/actions';
import queryString from "query-string";
import ErrorScreen from "../../ErrorScreens";
import { selectSessionContextOidc } from '@container/AdminPanel/features/users/usersSelectors';
import { useModules } from '../../utils/modules';
import useLiveRefresh from '../../utils/useLiveRefresh';

const ActivityListing = (props) => {
    const dispatch = useDispatch();
    const { isModuleEnabled } = useModules();
    const activityListingData = useSelector(selectEnrichedActivityListingData);
    const isLoading = useSelector(state => state.isLoading);
    const isFetchingListing = useSelector(selectIsActivityListingLoading);
    const toast = useSelector(state => state.toast);
    const pagingModel = useSelector(selectPagingModel);
    const dialogueBox = useSelector(state => state.dialogueBox);
    const appError = useSelector(state => state.appError);

    const sessionContext = useSelector(selectSessionContextOidc);
    const resolveActivityListingView = (isGridPref) =>
        isGridPref || !isModuleEnabled("activities.default_grid_view")
            ? ListingViewConstants.ListView
            : ListingViewConstants.CardView;
    const [selectedTab, setSelectedTab] = React.useState("open");
    const [view, setView] = React.useState(resolveActivityListingView(sessionContext.IsActivityGridView));
    const [searchText, setSearchText] = React.useState('');
    const [sortBy, setSortBy] = React.useState(1);
    const [isSortDesc, setIsSortDesc] = React.useState(true);
    const userView = sessionContext?.userView;

    const stateRef = React.useRef({});  
    stateRef.current = { selectedTab, view, searchText, sortBy, isSortDesc };

    const fetchListingData = React.useCallback(() => {
        const { selectedTab, view, searchText, sortBy, isSortDesc } = stateRef.current;
        const trimmedQuery = searchText.toLocaleLowerCase().trim();
        const activityListingPayload = {
            key: trimmedQuery,
            skip: 0,
            rows: sessionContext.IsActivityGridView ? 50 : 100,
            page: 1,
            status: selectedTab,
            isSearch: trimmedQuery.length > 0,
            sortDir: isSortDesc ? "DESC" : "ASC",
            sortBy: sortBy,
            take: sessionContext.IsActivityGridView ? 50 : 100,
            view: view,
            _: + new Date
        };
        dispatch(fetchActivityListingDataRequest(activityListingPayload));
    }, [sessionContext.IsActivityGridView]);

    // A live activity or note event refetches this listing with the tab/sort
    // arguments it currently owns.
    useLiveRefresh(['activity', 'note'], () => fetchListingData());

    const debouncedSearch = React.useMemo(() => debounce(() => fetchListingData(), 500), []);

    React.useEffect(() => {
        document.body.style.overflow = "hidden";
        const values = queryString.parse(props?.location?.search);
        if (values.st) {
            setSelectedTab(values.st);
        } else {
            fetchListingData();
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, []);

    const prevDialogueFlagRef = React.useRef(dialogueBox.flag);
    React.useEffect(() => {
        if (prevDialogueFlagRef.current === true && dialogueBox.flag === false) {
            dispatch(showDialogueBox({ flag: false, title: null, dialogueBoxComponent: null }));
        }
        prevDialogueFlagRef.current = dialogueBox.flag;
    }, [dialogueBox.flag]);

    const prevAppErrorStatusRef = React.useRef(appError.status);
    React.useEffect(() => {
        if (appError.status && appError.status !== prevAppErrorStatusRef.current) {
            if (appError.status == 105) {
                window.location.reload();
            } else if (appError.isErrorScreen) {
                dispatch(showDialogueBox({
                    flag: true,
                    component: <ErrorScreen status={appError.status} history={props.history} />,
                    overlayClassName: "modal animated faster fadeIn",
                    contentClassName: "modalContent globalSearchModalContent modalFixed animated faster fadeInUpx2 medium",
                    shouldCloseOnOverlayClick: true
                }));
            }
        }
        prevAppErrorStatusRef.current = appError.status;
    }, [appError]);

    const prevIsGridViewRef = React.useRef(sessionContext.IsActivityGridView);
    React.useEffect(() => {
        if (prevIsGridViewRef.current !== undefined && prevIsGridViewRef.current !== sessionContext.IsActivityGridView) {
            setView(resolveActivityListingView(sessionContext.IsActivityGridView));
        }
        prevIsGridViewRef.current = sessionContext.IsActivityGridView;
    }, [sessionContext.IsActivityGridView]);

    React.useEffect(() => {
        fetchListingData();
    }, [selectedTab, view, sortBy, isSortDesc]);

    const onTabClick = (selectedtab) => {
        setSelectedTab(selectedtab);
        setSearchText('');
    };

    const handleViewChange = (newView) => {
        setView(newView);
    };

    const handleSorting = (value) => {
        setSortBy(value);
        setIsSortDesc(true);
    };

    const toggleSortingDir = (value, newIsSortDesc) => {
        setIsSortDesc(typeof newIsSortDesc !== "undefined" ? newIsSortDesc : prev => !prev);
        if (value) setSortBy(value);
    };

    const SCROLL_PAGE_SIZE = 100;

    const onScrollHandler = () => {
        if (isFetchingListing) return;

        const { page, total, activitiesFullData } = activityListingData;
        const loadedCount = activitiesFullData?.length ?? 0;
        if (loadedCount >= total) return;

        const trimmedQuery = stateRef.current.searchText.toLocaleLowerCase().trim();
        const nextTake = Math.min(SCROLL_PAGE_SIZE, total - loadedCount);
        const payload = {
            key: trimmedQuery,
            skip: loadedCount,
            take: nextTake,
            rows: loadedCount,
            page: page + 1,
            status: activityListingData.status,
            isSearch: trimmedQuery.length > 0,
            sortDir: activityListingData.sortDir,
            sortBy: activityListingData.sortBy,
            view: stateRef.current.view,
            isScroll: true,
            _: + new Date
        };
        dispatch(fetchActivityListingDataRequest(payload));
    };

    const sortOptions = [
        {label: "Created date", value: 1},
        {label: "Skillset", value: 3},
        {label: "Due date", value: 4}
    ];

    return (
        <div className="detailPage">
            {isLoading &&
                <LoaderComponent />
            }
            {
                sessionContext && userView &&
                <CardListing
                    type={ListingViewConstants.Activity}
                    selectedTab={selectedTab}
                    data={{
                        ...activityListingData,
                        ActivitiesFullData: activityListingData.activitiesFullData,
                        Total: activityListingData.total,
                    }}
                    view={view}
                    pagingModel={pagingModel}
                     onScrollHandler={onScrollHandler}
                    tabCounts={activityListingData?.tabCounts || {openCount: activityListingData?.total, closeCount: 0, unassignedCount: 0, watchingCount: 0}}
                    sessionContext={sessionContext}
                    onTabClick={onTabClick}
                    sortOptions={sortOptions}
                    onChangeSearchQuery={(e) => setSearchText(e.target.value)}
                    search={debouncedSearch}
                    sortBy={sortBy}
                    handleSorting={handleSorting}
                    isSortDesc={isSortDesc}
                    toggleSortingDir={toggleSortingDir}
                    handleViewChange={handleViewChange}
                    searchText={searchText.toLocaleLowerCase()}
                />
            }

            {toast.show &&
                <Toast
                    message={toast.message}
                    type={toast.type}
                    delayTime={toast.delayTime}
                    closeToast={() => dispatch(hideToast())}
                    show={toast.show}
                    entityType={toast.entityType}
                    cId={toast.cId}
                    actId={toast.actId}
                    onViewToastClick={props.onViewToastClick}
                    showActions={toast.showActions}
                    isCloseIcon={toast.isCloseIcon}
                    autoClose={toast.autoClose}
                >
                </Toast>
            }
        </div>
    );
};

export default ActivityListing;